import React, { useMemo, useRef, useState } from 'react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { FiCopy, FiDownload, FiX, FiCheck, FiEdit2 } from 'react-icons/fi';
import { copyText } from '../../utils/clipboard';
import { showAlert, showConfirm } from '../common/AppDialog';
import { be1Fetch } from '../../config/api';
import { formatDateTime } from '../../utils/formatters';
import { Trans, useTranslation } from 'react-i18next';
import { errorText, errorFromResponse } from '../../services/errorText';

/** 서버가 돌려주는 수정본 요약 (/api/canvas/preview 의 override) */
export interface DagOverrideInfo {
  code: string;
  active: boolean;
  savedAt?: string | null;
  savedBy?: string | null;
  /** 수정본을 만든 뒤 캔버스가 바뀌었다 */
  stale?: boolean | null;
}

interface DagCodeModalProps {
  dagId: string;
  file?: string;
  /** 지금 캔버스가 만든 코드 */
  code: string;
  deployed?: boolean;
  override?: DagOverrideInfo | null;
  /** 'local' 이면 무설치판 — 수정본에서 쓸 수 있는 오퍼레이터가 제한된다 */
  engine?: 'local' | 'airflow';
  /** 수정본 저장·삭제에 보낼 캔버스 (워크플로 id·노드·엣지·설정) */
  canvasBody: Record<string, any>;
  onClose: () => void;
  /** 수정본이 바뀌었다 — 다음 실행 때 다시 배포해야 한다 */
  onOverrideChanged: (o: DagOverrideInfo | null) => void;
  /** 저장한 뒤 곧바로 실행 */
  onRun?: () => void;
}

const fmt = (s?: string | null) => formatDateTime(s, { dash: '' });

/**
 * DAG 코드 — 캔버스가 만든 코드 보기와 **수정본** 편집.
 *
 * 수정본을 켜 두면 배포·실행이 캔버스 대신 그 코드를 쓴다. 캔버스를 그 뒤에 고치면 수정본에는 반영되지 않으므로
 * 그 사실을 눈에 띄게 알리고, '캔버스 코드로 다시 시작' 으로 새로 뜰 수 있게 한다.
 */
const DagCodeModal: React.FC<DagCodeModalProps> = ({ dagId, file, code, deployed, override, engine, canvasBody, onClose, onOverrideChanged, onRun }) => {
  const { t } = useTranslation();
  const [ov, setOv] = useState<DagOverrideInfo | null>(override || null);
  const [tab, setTab] = useState<'canvas' | 'override'>(override?.active ? 'override' : 'canvas');
  const [draft, setDraft] = useState<string | null>(null);        // null 이면 보기 모드
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);

  const editing = draft !== null;
  const shown = editing ? draft! : tab === 'override' && ov ? ov.code : code;
  const lines = useMemo(() => shown.split('\n'), [shown]);
  const dirty = editing && draft !== (ov?.code ?? code);

  const close = async () => {
    if (dirty && !(await showConfirm(t('dialog:dagCode.discardClose'), { title: t('dialog:dagCode.name'), okText: t('common:ui.close') }))) return;
    onClose();
  };
  useEscapeKey(close);

  const copy = async () => {
    if (await copyText(shown)) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
    else showAlert(t('dialog:dagCode.copyFailed'), { title: t('dialog:dagCode.copyFailedTitle'), tone: 'warning' });
  };
  const download = () => {
    const blob = new Blob([shown], { type: 'text/x-python' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${dagId}.py`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const startEdit = (from: 'override' | 'canvas') => {
    setMessage(null);
    setTab('override');
    setDraft(from === 'override' && ov ? ov.code : code);
    setTimeout(() => editorRef.current?.focus(), 0);
  };

  const save = async (thenRun = false) => {
    if (draft === null) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await be1Fetch('/api/canvas/code-override', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...canvasBody, code: draft, active: true }),
      }, { retries: 0 });
      const body = await res.json();
      if (!res.ok) throw new Error(errorText(body, res.statusText));
      const next: DagOverrideInfo = body.override;
      setOv(next);
      setDraft(null);
      onOverrideChanged(next);
      const check = body.check || {};
      const extra = check.unmapped?.length ? t('dialog:dagCode.unmapped', { n: check.unmapped.length, tasks: check.unmapped.join(', ') }) : '';
      setMessage({ tone: 'ok', text: t('dialog:dagCode.saved', { tasks: check.tasks ? t('dialog:dagCode.taskCount', { n: check.tasks.length }) : '', extra }) });
      if (thenRun && onRun) { onClose(); onRun(); }
    } catch (e) {
      setMessage({ tone: 'error', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (active: boolean) => {
    setBusy(true);
    try {
      const res = await be1Fetch(`/api/canvas/${dagId}/code-override`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ active }),
      }, { retries: 0 });
      const body = await res.json();
      if (!res.ok) throw new Error(errorText(body, res.statusText));
      const next = ov ? { ...ov, active: body.active } : null;
      setOv(next);
      onOverrideChanged(next);
      setMessage({ tone: 'ok', text: active ? t('dialog:dagCode.turnedOn') : t('dialog:dagCode.turnedOff') });
    } catch (e) {
      setMessage({ tone: 'error', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!(await showConfirm(t('dialog:dagCode.deleteConfirm'), { title: t('dialog:dagCode.deleteTitle'), okText: t('common:ui.delete'), tone: 'danger' }))) return;
    setBusy(true);
    try {
      const res = await be1Fetch(`/api/canvas/${dagId}/code-override`, { method: 'DELETE' }, { retries: 0 });
      if (!res.ok) throw await errorFromResponse(res);
      setOv(null);
      setDraft(null);
      setTab('canvas');
      onOverrideChanged(null);
      setMessage({ tone: 'ok', text: t('dialog:dagCode.deleted') });
    } catch (e) {
      setMessage({ tone: 'error', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  /** Tab 은 들여쓰기(공백 4칸) — 파이썬 코드라 포커스 이동보다 들여쓰기가 필요하다 */
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      const el = e.currentTarget;
      const { selectionStart: s, selectionEnd: e2 } = el;
      const next = `${el.value.slice(0, s)}    ${el.value.slice(e2)}`;
      setDraft(next);
      requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = s + 4; });
    } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
      e.preventDefault();
      if (!busy) save(false);
    }
  };

  const btn = 'flex items-center gap-1 px-2.5 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50';
  const tabBtn = (active: boolean) => `px-3 py-1.5 text-sm border-b-2 ${active ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-gray-500 hover:text-gray-800'}`;

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-50" onMouseDown={e => { if (e.target === e.currentTarget) close(); }}>
      <div className="w-[1000px] max-w-[95vw] h-[84vh] bg-white rounded-lg shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gray-50 shrink-0">
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 truncate">{t('dialog:dagCode.title', { dagId })}</div>
            <div className="text-xs text-gray-500 truncate">
              {deployed ? t('dialog:dagCode.deployed') : t('dialog:dagCode.preview')}{file ? ` · ${file}` : ''}{t('dialog:dagCode.lines', { n: lines.length })}
              {ov && <>{t('dialog:dagCode.override')}{ov.active ? <b className="text-amber-700">{t('dialog:dagCode.inUse')}</b> : t('dialog:dagCode.notInUse')}{ov.savedAt ? ` (${fmt(ov.savedAt)}${ov.savedBy ? ` · ${ov.savedBy}` : ''})` : ''}</>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={copy} className={btn} title={t('dialog:dagCode.copyTitle')}>
              {/* @ts-ignore react-icons IconType 과 React 18 타입 불일치 회피 */}
              {copied ? <FiCheck className="text-green-600" /> : <FiCopy />} {copied ? t('dialog:dagCode.copied') : t('dialog:dagCode.copy')}
            </button>
            <button type="button" onClick={download} className={btn} title={t('dialog:dagCode.downloadTitle', { file: `${dagId}.py` })}>
              {/* @ts-ignore */}
              <FiDownload /> {t('dialog:dagCode.download')}
            </button>
            <button type="button" onClick={close} className="p-1.5 rounded text-gray-500 hover:bg-gray-100" title={t('common:ui.close')}>
              {/* @ts-ignore */}
              <FiX />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between px-4 border-b border-gray-200 shrink-0">
          <div className="flex">
            <button type="button" className={tabBtn(tab === 'canvas' && !editing)} disabled={editing} onClick={() => setTab('canvas')}>{t('dialog:dagCode.canvasTab')}</button>
            <button type="button" className={tabBtn(tab === 'override' || editing)} disabled={!ov && !editing} onClick={() => setTab('override')}
              title={ov ? '' : t('dialog:dagCode.noOverrideTitle')}>
              {t('dialog:dagCode.overrideTab')}{ov?.active ? ' ●' : ''}
            </button>
          </div>
          <div className="flex items-center gap-2 py-1.5">
            {editing ? (
              <>
                <button type="button" className={btn} disabled={busy} onClick={async () => { if (!dirty || await showConfirm(t('dialog:dagCode.discardEdit'), { title: t('dialog:dagCode.name'), okText: t('dialog:dagCode.discard') })) { setDraft(null); setTab(ov ? 'override' : 'canvas'); } }}>{t('common:ui.cancel')}</button>
                <button type="button" className={btn} disabled={busy} onClick={() => save(false)} title="Ctrl+S">{t('dialog:dagCode.save')}</button>
                {onRun && <button type="button" className="px-2.5 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50" disabled={busy} onClick={() => save(true)}>{t('dialog:dagCode.saveAndRun')}</button>}
              </>
            ) : (
              <>
                {ov && <label className="flex items-center gap-1 text-sm text-gray-700 mr-1" title={t('dialog:dagCode.runWithOverrideTitle')}>
                  <input type="checkbox" checked={ov.active} disabled={busy} onChange={(e) => toggle(e.target.checked)} /> {t('dialog:dagCode.runWithOverride')}
                </label>}
                {ov && <button type="button" className={btn} disabled={busy} onClick={remove}>{t('dialog:dagCode.deleteTitle')}</button>}
                <button type="button" className={btn} disabled={busy} onClick={() => startEdit(ov && tab === 'override' ? 'override' : 'canvas')}
                  title={ov && tab === 'override' ? t('dialog:dagCode.editOverrideTitle') : t('dialog:dagCode.fromCanvasTitle')}>
                  {/* @ts-ignore */}
                  <FiEdit2 /> {ov && tab === 'override' ? t('dialog:dagCode.editOverride') : ov ? t('dialog:dagCode.restartFromCanvas') : t('dialog:dagCode.edit')}
                </button>
              </>
            )}
          </div>
        </div>

        {ov?.stale && (
          <div className="px-4 py-2 text-xs bg-amber-50 text-amber-800 border-b border-amber-200 shrink-0">
            <Trans i18nKey="dialog:dagCode.stale" components={{ b: <b /> }} />
            {ov.active ? t('dialog:dagCode.staleActive') : '.'}{t('dialog:dagCode.staleHint')}
          </div>
        )}
        {editing && (
          <div className="px-4 py-1.5 text-xs bg-blue-50 text-blue-800 border-b border-blue-100 shrink-0">
            {t('dialog:dagCode.editingHint')}
            {engine === 'local' && t('dialog:dagCode.localLimit')}
          </div>
        )}
        {message && (
          <div className={`px-4 py-1.5 text-xs border-b shrink-0 whitespace-pre-wrap ${message.tone === 'ok' ? 'bg-green-50 text-green-800 border-green-100' : 'bg-red-50 text-red-700 border-red-100'}`}>
            {message.text}
          </div>
        )}

        {editing ? (
          <div className="flex-1 min-h-0 flex bg-gray-50 font-mono text-[0.8125rem] leading-5">
            <div ref={gutterRef} className="select-none overflow-hidden text-right text-gray-400 border-r border-gray-200 py-2 w-14 shrink-0">
              {lines.map((_, i) => <div key={i} className="pr-3">{i + 1}</div>)}
            </div>
            <textarea
              ref={editorRef}
              value={draft!}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              onScroll={(e) => { if (gutterRef.current) gutterRef.current.scrollTop = e.currentTarget.scrollTop; }}
              spellCheck={false}
              wrap="off"
              className="flex-1 min-w-0 resize-none bg-white text-gray-800 py-2 px-3 outline-none leading-5 font-mono text-[0.8125rem] whitespace-pre overflow-auto"
              aria-label={t('dialog:dagCode.editorLabel')}
            />
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto bg-gray-50">
            <table className="font-mono text-[0.8125rem] leading-5 border-collapse w-full">
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="hover:bg-gray-100">
                    <td className="select-none text-right pr-3 pl-3 text-gray-400 w-12 align-top border-r border-gray-200">{i + 1}</td>
                    <td className="pl-3 pr-4 whitespace-pre text-gray-800">{l}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DagCodeModal;
